import { BrokenInAppBrowser } from "./detect";
import {
  EscapeAttempt,
  buildEscapeAttempts,
  escapeAttemptDelayMs,
} from "./escapeAttempts";
import { EscapeTrigger, reportEscapeOutcome } from "./reportEscape";

type RunInAppEscapeParams = {
  browser: BrokenInAppBrowser;
  destination: string;
  /** Every scheme fired and the page never went away. */
  onExhausted?: () => void;
  /** An escape worked and the visitor has since come back to the webview. */
  onReturned?: () => void;
  /** Set from a tap handler, which unlocks the attempts needing a gesture. */
  hasGesture?: boolean;
  /** Falls back to the click published for this page. */
  clickId?: string | null;
};

/**
 * Fires the schemes in order until one takes the visitor out, then reports
 * back. Returns a disposer.
 *
 * The first scheme fires synchronously so that a call from a tap handler keeps
 * the user activation iOS requires; an await in between loses it. Prefer the
 * useInAppEscape hook, which owns the disposer.
 */
export function runInAppEscape({
  browser,
  destination,
  onExhausted,
  onReturned,
  hasGesture = false,
  clickId,
}: RunInAppEscapeParams): () => void {
  const attempts = buildEscapeAttempts(browser, destination).filter(
    (attempt) => hasGesture || !attempt.needsGesture
  );

  // Which pass produced the outcome, so taps and the automatic pass can be
  // told apart in the data.
  const trigger: EscapeTrigger = hasGesture ? "tap" : "auto";

  let index = 0;
  let timer: number | null = null;
  let disposed = false;
  let disposeReturnWatch: (() => void) | null = null;
  let hasReported = false;

  // Reset before every attempt: one false positive would otherwise bury the
  // rest of the chain.
  let hasLeft = false;

  // Reported from the handlers, not from succeed(): once another app is in
  // front, iOS may never grant the timer tick succeed() waits for.
  const reportLeft = () => {
    if (hasReported || index === 0) return;
    hasReported = true;

    // index already points past the attempt that fired.
    const attempt = attempts[index - 1];
    reportEscapeOutcome({
      clickId,
      browser,
      trigger,
      result: {
        outcome: "left",
        scheme: attempt.scheme,
        attemptIndex: index - 1,
      },
      isLeaving: true,
    });
  };

  const markLeft = () => {
    hasLeft = true;
    reportLeft();
  };

  // Not `blur`: a custom scheme blurs the page even when nothing handles it,
  // which would report success for every failed attempt.
  const handleVisibility = () => {
    if (document.visibilityState !== "hidden") return;
    hasLeft = true;
    reportLeft();
  };

  const stopWatching = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("pagehide", markLeft);
  };

  const succeed = () => {
    stopWatching();
    if (!onReturned) return;

    // iOS suspends timers while another app is in front, so this tick may have
    // been held for the whole store visit. Waiting for a visibilitychange would
    // then wait for something that already happened.
    if (document.visibilityState === "visible") {
      onReturned();
      return;
    }

    // Still away, and this page would spin forever if they came back to it.
    const handleReturn = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", handleReturn);
      onReturned();
    };
    document.addEventListener("visibilitychange", handleReturn);
    disposeReturnWatch = () =>
      document.removeEventListener("visibilitychange", handleReturn);
  };

  const fire = (attempt: EscapeAttempt) => {
    try {
      if (attempt.via === "window") {
        window.open(attempt.url, "_blank");
        return;
      }
      window.location.href = attempt.url;
    } catch {
      // Unknown schemes throw outright in some webviews.
    }
  };

  const step = () => {
    if (disposed) return;

    if (hasLeft || document.visibilityState === "hidden") {
      succeed();
      return;
    }

    if (index >= attempts.length) {
      stopWatching();

      // The page is still here, so this can go out over a normal request.
      if (!hasReported) {
        hasReported = true;
        reportEscapeOutcome({
          clickId,
          browser,
          trigger,
          result: { outcome: "exhausted" },
          isLeaving: false,
        });
      }

      onExhausted?.();
      return;
    }

    hasLeft = false;
    const delay = escapeAttemptDelayMs(index, attempts.length);
    fire(attempts[index]);
    index += 1;

    timer = window.setTimeout(step, delay);
  };

  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", markLeft);

  // Sent before the first attempt, while the page is still in front. Anything
  // after this can be lost to a departure.
  reportEscapeOutcome({
    clickId,
    browser,
    trigger,
    result: { outcome: "started", attempts: attempts.length },
    isLeaving: false,
  });

  step();

  return () => {
    disposed = true;
    stopWatching();
    disposeReturnWatch?.();
  };
}
